<template>
  <div class="product-details">
    <div class="container">
      <!-- Breadcrumbs -->
      <div class="breadcrumb-nav mb-4">
        <span>Home / Mobile Phones / {{ product.category_name || 'Products' }} / {{ product.product_name }}</span>
      </div>

      <div class="row">
        <!-- Left Column: Product Images -->
        <div class="col-xl-6">
          <div class="product-image-gallery">
            <!-- Thumbnail Navigation (Left Side) - Always shows normal product images -->
            <!-- Only show if there are thumbnails (excluding the active image) -->
            <div class="thumbnail-navigation" v-if="getThumbnailImages().length > 0">
              <button 
                v-if="getThumbnailImages().length > 4"
                class="nav-arrow nav-arrow-up"
                @click="scrollThumbnails('up')"
                :disabled="thumbnailScrollPosition === 0"
                title="Scroll up"
              >
                <i class="fas fa-chevron-up"></i>
              </button>
              <div class="thumbnail-list" ref="thumbnailListRef">
                <button 
                  v-for="(img, i) in getThumbnailImages()" 
                  :key="`thumb-${i}-${img}`"
                  @click="selectImage(img)"
                  :class="`thumbnail-item ${img === active_img ? 'active' : ''}`"
                  :title="`Image ${i + 1}`"
                >
                  <div class="thumbnail-image-wrapper">
                    <img :src="img" :alt="`Thumbnail ${i + 1}`" @error="handleThumbnailError" />
                    <div class="thumbnail-overlay"></div>
                  </div>
                </button>
              </div>
              <button 
                v-if="getThumbnailImages().length > 4"
                class="nav-arrow nav-arrow-down"
                @click="scrollThumbnails('down')"
                :disabled="thumbnailScrollPosition >= getThumbnailImages().length - 4"
                title="Scroll down"
              >
                <i class="fas fa-chevron-down"></i>
              </button>
            </div>
            
            <!-- Main Product Image Area -->
            <div class="main-image-container">
              <div class="main-image-wrapper" @mouseenter="showZoom = zoomEnabled" @mouseleave="showZoom = false">
                <div class="image-loader" v-if="imageLoading">
                  <div class="spinner"></div>
                </div>
                <img 
                  v-if="hasValidImage"
                  :src="validImageSrc" 
                  alt="product-img" 
                  class="main-product-image"
                  :class="{ 'zoom-enabled': showZoom }"
                  @load="handleImageLoad"
                  @error="handleImageError"
                  ref="mainImageRef"
                />
                <div v-else class="no-image-placeholder">
                  <i class="fas fa-image"></i>
                  <p>No image available</p>
                </div>
                <div 
                  class="image-zoom-overlay" 
                  v-if="zoomEnabled" 
                  :class="{ 'zoom-active': zoomEnabled }" 
                  @mousemove="handleZoom"
                  @click="zoomOut"
                  ref="zoomOverlayRef"
                  title="Click to zoom out"></div>
              </div>
              
              <!-- Image Navigation Arrows -->
              <button 
                v-if="getDisplayImages().length > 1"
                class="image-nav-btn image-nav-prev"
                @click="navigateImage('prev')"
                title="Previous image"
              >
                <i class="fas fa-chevron-left"></i>
              </button>
              <button 
                v-if="getDisplayImages().length > 1"
                class="image-nav-btn image-nav-next"
                @click="navigateImage('next')"
                title="Next image"
              >
                <i class="fas fa-chevron-right"></i>
              </button>
              
              <!-- Image Actions -->
              <div class="image-actions">
                <button class="action-btn" @click="openImageModal" title="View Full Screen">
                  <i class="fas fa-expand"></i>
                </button>
                <button 
                  v-if="zoomLevel < 3" 
                  class="action-btn" 
                  @click="zoomIn" 
                  title="Zoom In">
                  <i class="fas fa-search-plus"></i>
                </button>
                <button 
                  v-if="zoomLevel > 1" 
                  class="action-btn" 
                  @click="zoomOut" 
                  title="Zoom Out">
                  <i class="fas fa-search-minus"></i>
                </button>
                <button 
                  v-if="zoomLevel > 1" 
                  class="action-btn" 
                  @click="resetZoom" 
                  title="Reset Zoom">
                  <i class="fas fa-undo"></i>
                </button>
              </div>
              
              <!-- Image Counter -->
              <div class="image-counter" v-if="getDisplayImages().length > 1">
                {{ currentImageIndex + 1 }} / {{ getDisplayImages().length }}
              </div>
            </div>
          </div>

          <!-- Color Thumbnails (if available) -->
          <div class="color-thumbnails mt-4" v-if="product.available_colors && product.available_colors.length > 0">
            <div class="d-flex gap-3 flex-wrap">
              <div 
                v-for="color in product.available_colors" 
                :key="color.id"
                class="color-thumb"
                :class="{ 'active': selectedColor && selectedColor.id === color.id }"
                @click="selectColor(color)"
                :title="color.name">
                <div class="color-thumb-image-wrapper">
                  <div class="color-thumb-image">
                    <img :src="getColorImage(color)" :alt="color.name" />
                  </div>
                  <div class="color-thumb-image color-thumb-image-duplicate">
                    <img :src="getColorImage(color)" :alt="color.name" />
                  </div>
                </div>
                <div class="color-thumb-label">{{ color.name }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Product Details -->
        <div class="col-xl-6">
          <div class="product__details-content">
            <!-- Product Title with Badges -->
            <div class="product-title-section mb-3">
              <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <div class="d-flex align-items-center flex-wrap gap-2">
                  <span class="badge discount-badge" v-if="product.discount_percentage > 0">
                    -{{ product.discount_percentage }}%
                  </span>
                  <span class="badge new-badge" v-if="product.type === 'Brand New'">NEW</span>
                </div>
                <div class="product-brand-logo" v-if="product.model_name">
                  <i class="fas fa-mobile-alt"></i>
                </div>
              </div>
              <h2 class="product-name mb-0">{{ product.product_name }}</h2>
            </div>

            <!-- Price -->
            <div class="price-section mb-4">
              <p class="price-section__label">Price</p>
              <div class="d-flex align-items-baseline flex-wrap gap-2">
                <h3 class="current-price mb-0">{{ currencySymbol }} {{ formatPrice(currentDisplayPrice) }}</h3>
                <span v-if="product.discount_percentage > 0" class="original-price text-decoration-line-through">
                  {{ currencySymbol }} {{ formatPrice(product.web_price) }}
                </span>
              </div>
            </div>

            <!-- Stock Status Badge - Show In Stock if available -->
            <div class="stock-status mb-4" v-if="isInStock()">
              <span class="badge stock-badge in-stock">
                <i class="fas fa-check-circle me-1"></i>
                In Stock
              </span>
            </div>
            <div class="stock-status mb-4" v-else>
              <span class="badge stock-badge out-of-stock">
                <i class="fas fa-times-circle me-1"></i>
                Out of Stock
              </span>
            </div>

            <!-- Color Selection -->
            <div class="selection-group mb-3" v-if="product.available_colors && product.available_colors.length > 0">
              <label class="form-label fw-bold">Colour:</label>
              <select 
                v-model="selectedColorId" 
                @change="onColorChange"
                class="form-select selection-input"
                :class="{ 'has-selection': selectedColorId }">
                <option :value="null">Choose an option</option>
                <option 
                  v-for="color in product.available_colors" 
                  :key="color.id"
                  :value="color.id">
                  {{ color.name }}
                </option>
              </select>
            </div>

            <!-- Storage Selection -->
            <div class="selection-group mb-3" v-if="getAvailableStoragesForColor().length > 0">
              <label class="form-label fw-bold">Storage:</label>
              <select 
                v-model="selectedStorageId" 
                @change="onStorageChange"
                class="form-select selection-input"
                :class="{ 'has-selection': selectedStorageId }"
                :disabled="!selectedColorId">
                <option :value="null">{{ selectedColorId ? 'Choose an option' : 'Please select a color first' }}</option>
                <option 
                  v-for="storage in getAvailableStoragesForColor()" 
                  :key="storage.id"
                  :value="storage.id">
                  {{ storage.name }}
                </option>
              </select>
            </div>

            <!-- Warranty Selection -->
            <div class="selection-group mb-3" v-if="product.warranties && product.warranties.length > 0">
              <label class="form-label fw-bold">Warranty:</label>
              <select 
                v-model="selectedWarranty" 
                class="form-select selection-input">
                <option :value="null">Choose an option</option>
                <option 
                  v-for="warranty in product.warranties" 
                  :key="warranty.id"
                  :value="warranty.id">
                  {{ warranty.name }}
                </option>
              </select>
            </div>

            <!-- Quantity Selector -->
            <div class="selection-group mb-4">
              <label class="form-label fw-bold">Quantity:</label>
              <div class="quantity-selector">
                <button 
                  @click="decrementQuantity" 
                  class="qty-btn"
                  :disabled="quantity <= 1 || !isInStock()">-</button>
                <input 
                  type="number" 
                  v-model.number="quantity" 
                  @input="validateQuantity"
                  :min="1"
                  :max="getMaxQuantity()"
                  class="qty-input">
                <button 
                  @click="incrementQuantity" 
                  class="qty-btn"
                  :disabled="!isInStock() || quantity >= getMaxQuantity()">+</button>
                </div>
              </div>

            <!-- Action Buttons -->
            <div class="action-buttons mb-4">
              <button 
                @click="addToCart" 
                class="btn btn-primary action-btn add-cart-btn"
                :disabled="!isInStock()">
                ADD TO CART
              </button>
              <button 
                @click="buyNow" 
                class="btn btn-primary action-btn buy-now-btn"
                :disabled="!isInStock()">
                BUY NOW
              </button>
            </div>

            <!-- Wishlist -->
            <div class="wishlist-link mb-4">
              <a href="#" @click.prevent="addToWishlist" class="wishlist-link-text">
                <i class="far fa-heart me-1"></i>Add to wishlist
                  </a>
                </div>

            <!-- Categories -->
            <div class="product-info-section mb-4">
              <div class="product-info-item">
                <span class="info-label fw-bold">Categories:</span>
                <span class="info-value">{{ product.category_name }}</span>
                <span v-if="product.model_name" class="info-value">, {{ product.model_name }}</span>
                </div>
              </div>

            <!-- Delivery Information -->
            <div class="delivery-info-section mb-4">
              <h6 class="delivery-title mb-3">
                <i class="fas fa-truck me-2"></i>Delivery Information (Estimated Delivery Time)
              </h6>
              <div class="delivery-item">
                <i class="fas fa-map-marker-alt me-2 text-primary"></i>
                <span>Colombo 1-15 (2-3 Hours)</span>
              </div>
              <div class="delivery-item">
                <i class="fas fa-map-marker-alt me-2 text-primary"></i>
                <span>Other Areas (2-3 Business Days)</span>
                </div>
              <div class="delivery-item mt-2">
                <i class="fas fa-store me-2 text-primary"></i>
                <span>Store Pickup: Between 10AM to 5PM Mon to Sat</span>
                </div>
              </div>

            <!-- Share Buttons -->
            <div class="share-section">
              <h6 class="share-title mb-2">Share:</h6>
              <div class="share-buttons">
                <a href="#" @click.prevent="shareOnFacebook" class="share-btn facebook" title="Share on Facebook">
                  <i class="fab fa-facebook-f"></i>
                </a>
                <a href="#" @click.prevent="shareOnTwitter" class="share-btn twitter" title="Share on Twitter">
                  <i class="fab fa-twitter"></i>
                </a>
                <a href="#" @click.prevent="shareOnWhatsApp" class="share-btn whatsapp" title="Share on WhatsApp">
                  <i class="fab fa-whatsapp"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Full Screen Image Modal -->
  <div v-if="showFullScreenModal" class="fullscreen-image-modal" @click="closeImageModal">
    <div class="fullscreen-image-container" @click.stop>
      <button class="close-fullscreen-btn" @click="closeImageModal">
        <i class="fas fa-times"></i>
      </button>
      <img :src="fullScreenImage" alt="Full Screen Product Image" class="fullscreen-image" />
    </div>
  </div>
</template>
  
  <script>
import { ref, watch, computed, nextTick } from 'vue';
  import { useCartStore } from '@/stores/useCartStore';
  import { useWishlistStore } from '@/stores/wishlistStore';
  import { useCurrency } from '@/composables/useCurrency';
  
  export default {
    props: {
    product: {
      type: Object,
      default: () => ({})
    }
    },
    setup(props) {
      const cartStore = useCartStore();
      const wishlistStore = useWishlistStore();
      const { currencySymbol, formatPrice: formatPriceCurrency } = useCurrency();
      
    const selectedColor = ref(null);
    const selectedColorId = ref(null);
    const selectedStorage = ref(null);
    const selectedStorageId = ref(null);
    const selectedWarranty = ref(props.product?.warranty_id || null);
    const quantity = ref(1);
    const selectedVariantPrice = ref(null);
    const selectedVariant = ref(null);
    
    // Track if user manually selected an image (to prevent watch from overriding)
    const userSelectedImage = ref(false);

    // Helper function to get thumbnail images (for initialization)
    // Returns all available images without filtering (used for initial setup)
    const getThumbnailImagesForInit = (product) => {
      const allImages = [];
      
      // Add main product images (is_main = 1)
      if (product?.images && product.images.length > 0) {
        allImages.push(...product.images);
      }
      
      // Add normal product images (is_main = 0, variant_id = null) - Always show these
      if (product?.normal_product_images && product.normal_product_images.length > 0) {
        allImages.push(...product.normal_product_images);
      }
      
      // Remove duplicates
      const uniqueImages = [...new Set(allImages)].filter(img => img && img.trim() !== '');
      
      if (uniqueImages.length > 0) {
        return uniqueImages;
      }
      
      // Last fallback to single product image
      if (product?.image && product.image.trim() !== '') {
        return [product.image];
      }
      
      return [];
    };

    // Initialize active_img with first thumbnail image if available
    const initializeActiveImage = () => {
      const thumbnailImages = getThumbnailImagesForInit(props.product);
      if (thumbnailImages.length > 0) {
        return thumbnailImages[0];
      }
      // Fallback to product.image if no thumbnails
      return props.product?.image || '';
    };

    const active_img = ref(initializeActiveImage());

    // Watch for product changes
    watch(() => props.product, (newProduct) => {
      if (!newProduct) return;
      
      // Priority: Use first thumbnail image (from left sidebar)
      const thumbnailImages = getThumbnailImagesForInit(newProduct);
      if (thumbnailImages.length > 0) {
        active_img.value = thumbnailImages[0];
      } else if (newProduct.images && newProduct.images.length > 0) {
        active_img.value = newProduct.images[0];
      } else if (newProduct.image) {
        active_img.value = newProduct.image;
      } else {
        active_img.value = '';
      }
      
      selectedWarranty.value = newProduct?.warranty_id || null;
      
      // Reset selections when product changes
      selectedColorId.value = null;
      selectedStorageId.value = null;
      selectedColor.value = null;
      selectedStorage.value = null;
      selectedVariantPrice.value = null;
      selectedVariant.value = null;
      quantity.value = 1;
    }, { immediate: true });
    
    // Watch for color selection changes to update main image area
    // Only auto-update if user hasn't manually selected an image
    watch(() => selectedColor.value, (newColor) => {
      // If user manually selected an image, don't override it
      if (userSelectedImage.value) {
        // Don't reset flag immediately - keep it for a while to prevent interference
        return;
      }
      
      if (newColor) {
        const variantImages = getVariantImages(newColor);
        if (variantImages.length > 0) {
          // Set first variant image as active in main area
          active_img.value = variantImages[0];
        } else {
          // Fallback: try to get variant image_url or image_path
          const variant = props.product?.variants?.find(v => {
            const colorMatch = v.color_id == newColor.id || 
                              String(v.color_id) === String(newColor.id) ||
                              parseInt(v.color_id) === parseInt(newColor.id);
            return colorMatch && (v.status === 'Active' || !v.status);
          });
          
          if (variant) {
            if (variant.image_url) {
              active_img.value = variant.image_url;
            } else if (variant.image_path) {
              const baseUrl = window.location.origin;
              active_img.value = `${baseUrl}/storage/${variant.image_path}`;
            } else {
              // Fallback to first normal product image
              const thumbnailImages = getThumbnailImages();
              if (thumbnailImages.length > 0) {
                active_img.value = thumbnailImages[0];
              } else if (props.product?.image) {
                active_img.value = props.product.image;
              }
            }
          } else {
            // Fallback to first normal product image
            const thumbnailImages = getThumbnailImages();
            if (thumbnailImages.length > 0) {
              active_img.value = thumbnailImages[0];
            } else if (props.product?.image) {
              active_img.value = props.product.image;
            }
          }
        }
      } else {
        // Reset to normal product image if no color selected
        const thumbnailImages = getThumbnailImages();
        if (thumbnailImages.length > 0) {
          active_img.value = thumbnailImages[0];
        } else if (props.product?.image) {
          active_img.value = props.product.image;
        }
      }
    });

    // Get color image - use variant images if available, otherwise use main product image
    const getColorImage = (color) => {
      if (!props.product?.variants) return props.product?.image || '';
      
      // Try to find variant by color ID first (more reliable)
      let variant = props.product.variants.find(v => {
        const colorMatch = v.color_id == color.id || 
                          String(v.color_id) === String(color.id) ||
                          parseInt(v.color_id) === parseInt(color.id);
        const statusMatch = v.status === 'Active' || !v.status;
        return colorMatch && statusMatch;
      });
      
      // If not found by ID, try by name
      if (!variant) {
        variant = props.product.variants.find(v => 
          v.color_name === color.name && (v.status === 'Active' || !v.status)
        );
      }
      
      if (variant) {
        // First check for variant_images array (uploaded images)
        if (variant.variant_images && variant.variant_images.length > 0) {
          return variant.variant_images[0]; // Return first variant image
        }
        
        // Use image_url if available (from API), otherwise try image_path
        if (variant.image_url) {
          return variant.image_url;
        }
        if (variant.image_path) {
          const baseUrl = window.location.origin;
          return `${baseUrl}/storage/${variant.image_path}`;
        }
      }
      
      return props.product?.image || '';
    };
    
    // Get all variant images for a color (max 5)
    const getVariantImages = (color) => {
      if (!props.product?.variants) return [];
      
      // Find variant by color
      let variant = props.product.variants.find(v => {
        const colorMatch = v.color_id == color.id || 
                          String(v.color_id) === String(color.id) ||
                          parseInt(v.color_id) === parseInt(color.id);
        const statusMatch = v.status === 'Active' || !v.status;
        return colorMatch && statusMatch;
      });
      
      if (!variant) {
        variant = props.product.variants.find(v => 
          v.color_name === color.name && (v.status === 'Active' || !v.status)
        );
      }
      
      if (variant && variant.variant_images && variant.variant_images.length > 0) {
        // Return maximum 5 images
        return variant.variant_images.slice(0, 5);
      }
      
      return [];
    };
    
    // Get images for thumbnail navigation (left side) - Always shows normal product images
    // Excludes the currently active image to avoid duplicates
    const getThumbnailImages = () => {
      const allImages = [];
      
      // Add main product images (is_main = 1)
      if (props.product?.images && props.product.images.length > 0) {
        allImages.push(...props.product.images);
      }
      
      // Add normal product images (is_main = 0, variant_id = null) - Always show these
      if (props.product?.normal_product_images && props.product.normal_product_images.length > 0) {
        allImages.push(...props.product.normal_product_images);
      }
      
      // Remove duplicates and filter out the currently active image
      const uniqueImages = [...new Set(allImages)];
      const filteredImages = uniqueImages.filter(img => {
        // Exclude the currently active image from thumbnails
        return img && img !== active_img.value && img.trim() !== '';
      });
      
      if (filteredImages.length > 0) {
        return filteredImages;
      }
      
      // If all images are filtered out (only active image exists), return empty array
      // This prevents showing duplicate in sidebar
      return [];
    };
    
    // Get images to display in the main area (center)
    // If color is selected, show variant images, otherwise show normal product images
    const getDisplayImages = () => {
      // If color is selected, get variant images for that color
      if (selectedColor.value) {
        const variantImages = getVariantImages(selectedColor.value);
        if (variantImages.length > 0) {
          return variantImages;
        }
      }
      
      // If no color selected, show normal product images in main area too
      return getThumbnailImages();
    };

    // Find matching variant based on selected color and storage
    const findMatchingVariant = () => {
      if (!props.product?.variants || !Array.isArray(props.product.variants)) {
        selectedVariantPrice.value = null;
        return null;
      }

      const variants = props.product.variants.filter(v => v.status === 'Active' || !v.status);
      
      if (variants.length === 0) {
        selectedVariantPrice.value = null;
        return null;
      }
      
      // Update selectedColor and selectedStorage objects from IDs
      // Handle both string and number ID comparisons
      if (selectedColorId.value && props.product.available_colors) {
        selectedColor.value = props.product.available_colors.find(c => {
          const cId = c.id;
          const sId = selectedColorId.value;
          const match = cId == sId || String(cId) === String(sId) || parseInt(cId) === parseInt(sId);
          if (match) {
            console.log('Color matched:', { colorId: cId, selectedId: sId, color: c });
          }
          return match;
        });
        if (!selectedColor.value) {
          console.log('Color not found in available_colors:', selectedColorId.value);
        }
      } else {
        selectedColor.value = null;
      }
      
      // ✅ FIX: Storage matching - handle both ID and name matching (storage_id can be null)
      if (selectedStorageId.value) {
        // First try to find in available_storages by ID
        if (props.product.available_storages) {
          selectedStorage.value = props.product.available_storages.find(s => {
            const sId = s.id;
            const stId = selectedStorageId.value;
            const match = sId == stId || String(sId) === String(stId) || parseInt(sId) === parseInt(stId);
            if (match) {
              console.log('Storage matched by ID:', { storageId: sId, selectedId: stId, storage: s });
            }
            return match;
          });
        }
        
        // If not found by ID, try matching by name (for cases where storage_id is null)
        if (!selectedStorage.value) {
          if (props.product.available_storages) {
            selectedStorage.value = props.product.available_storages.find(s => {
              const match = s.name === selectedStorageId.value || 
                           s.name?.toLowerCase() === selectedStorageId.value?.toLowerCase();
              if (match) {
                console.log('Storage matched by name:', { storageName: s.name, selectedId: selectedStorageId.value, storage: s });
              }
              return match;
            });
          }
          
          // If still not found, try to find from variants directly
          if (!selectedStorage.value && props.product.variants) {
            const variantWithStorage = props.product.variants.find(v => 
              v.storage_name === selectedStorageId.value ||
              v.storage_name?.toLowerCase() === selectedStorageId.value?.toLowerCase()
            );
            if (variantWithStorage) {
              selectedStorage.value = {
                id: variantWithStorage.storage_id || variantWithStorage.storage_name,
                name: variantWithStorage.storage_name
              };
              console.log('Storage found from variant:', selectedStorage.value);
            }
          }
        }
        
        if (!selectedStorage.value) {
          console.log('Storage not found in available_storages or variants:', selectedStorageId.value);
        }
      } else {
        selectedStorage.value = null;
      }
      
      // If both color and storage are selected, find exact match
      if (selectedColor.value && selectedStorage.value) {
        console.log('=== Finding Variant ===');
        console.log('Selected Color:', selectedColor.value);
        console.log('Selected Storage:', selectedStorage.value);
        console.log('Available Variants:', variants);
        
        const variant = variants.find(v => {
          // Try matching by ID first (more reliable)
          const colorMatchById = v.color_id && (
            v.color_id == selectedColor.value.id || 
            String(v.color_id) === String(selectedColor.value.id) ||
            parseInt(v.color_id) === parseInt(selectedColor.value.id)
          );
          
          // ✅ FIX: Storage matching - handle both ID and name (storage_id can be null)
          const storageMatchById = v.storage_id && selectedStorage.value.id && (
            v.storage_id == selectedStorage.value.id || 
            String(v.storage_id) === String(selectedStorage.value.id) ||
            parseInt(v.storage_id) === parseInt(selectedStorage.value.id)
          );
          
          // Also try matching by name (fallback - especially important when storage_id is null)
          const colorMatchByName = v.color_name && (
            v.color_name === selectedColor.value.name ||
            v.color_name?.toLowerCase() === selectedColor.value.name?.toLowerCase()
          );
          
          // ✅ FIX: Storage name matching - handle both selectedStorage.value.name and selectedStorageId.value
          const storageMatchByName = v.storage_name && (
            v.storage_name === selectedStorage.value.name ||
            v.storage_name?.toLowerCase() === selectedStorage.value.name?.toLowerCase() ||
            v.storage_name === selectedStorageId.value ||
            v.storage_name?.toLowerCase() === selectedStorageId.value?.toLowerCase()
          );
          
          const colorMatch = colorMatchById || colorMatchByName;
          // ✅ FIX: Use name matching if ID matching fails (important when storage_id is null)
          const storageMatch = storageMatchById || storageMatchByName;
          
          const isMatch = colorMatch && storageMatch;
          if (isMatch) {
            console.log('✅ MATCH FOUND:', {
              variant_id: v.variant_id || v.id,
              color_id: v.color_id,
              color_name: v.color_name,
              storage_id: v.storage_id,
              storage_name: v.storage_name,
              price: v.price,
              old_product_id: v.old_product_id,
              old_product_price: v.old_product_price,
              price_type: typeof v.price
            });
          }
          return isMatch;
        });
        
        if (variant) {
          // Store the selected variant
          selectedVariant.value = variant;
          console.log('💰 Variant found - Determining price:', {
            variant_id: variant.variant_id || variant.id,
            color: variant.color_name,
            storage: variant.storage_name,
            old_product_id: variant.old_product_id,
            old_product_price: variant.old_product_price,
            variant_price: variant.price,
            variant_web_price: variant.web_price,
            product_web_price: props.product?.web_price
          });
          
          // ✅ FIX: Priority order: old_product_price → variant.price → variant.web_price → product.web_price
          let finalPrice = null;
          
          // Priority 1: If variant has old_product_id, use old_product_price first
          if (variant.old_product_id && variant.old_product_price !== null && variant.old_product_price !== undefined) {
            const oldProductPrice = parseFloat(variant.old_product_price);
            if (!isNaN(oldProductPrice) && oldProductPrice > 0) {
              finalPrice = oldProductPrice;
              console.log('✅ Using old product price from migrated product:', {
                old_product_id: variant.old_product_id,
                old_product_name: variant.old_product_name,
                old_product_price: oldProductPrice
              });
            }
          }
          
          // Priority 2: If old_product_price not available, use variant's own price
          if (finalPrice === null && variant.price !== undefined && variant.price !== null && variant.price !== '') {
            const variantPrice = parseFloat(variant.price);
            if (!isNaN(variantPrice) && variantPrice > 0) {
              finalPrice = variantPrice;
              console.log('✅ Using variant own price:', variantPrice);
            }
          }
          
          // Priority 3: Fallback to variant's web_price (if available)
          if (finalPrice === null && variant.web_price !== undefined && variant.web_price !== null) {
            const variantWebPrice = parseFloat(variant.web_price);
            if (!isNaN(variantWebPrice) && variantWebPrice > 0) {
              finalPrice = variantWebPrice;
              console.log('✅ Using variant web_price:', variantWebPrice);
            }
          }
          
          // Priority 4: Final fallback to product web_price
          if (finalPrice === null && props.product?.web_price) {
            const webPrice = parseFloat(props.product.web_price);
            if (!isNaN(webPrice) && webPrice > 0) {
              finalPrice = webPrice;
              console.log('✅ Using product web_price:', webPrice);
            }
          }
          
          // Set the final price
          if (finalPrice !== null) {
            selectedVariantPrice.value = finalPrice;
            console.log('✅ Final price set to:', finalPrice);
          } else {
            selectedVariantPrice.value = null;
            console.log('❌ No valid price found for variant');
          }
          // Only update main image if user hasn't manually selected an image
          if (!userSelectedImage.value) {
            if (variant.image_url) {
              active_img.value = variant.image_url;
            } else if (variant.image_path) {
              const baseUrl = window.location.origin;
              active_img.value = `${baseUrl}/storage/${variant.image_path}`;
            }
          }
          return variant;
        } else {
          // Clear selected variant if no match found
          selectedVariant.value = null;
          console.log('❌ NO MATCHING VARIANT FOUND');
          console.log('Searched for:', {
            colorId: selectedColor.value.id,
            colorName: selectedColor.value.name,
            storageId: selectedStorage.value.id,
            storageName: selectedStorage.value.name
          });
          selectedVariantPrice.value = null;
        }
      }
      
      // If only color is selected, don't set price - wait for storage selection
      if (selectedColor.value && !selectedStorage.value) {
        // Clear selected variant since we need both color and storage
        selectedVariant.value = null;
        const variant = variants.find(v => {
          return v.color_name === selectedColor.value.name || 
                 (v.color_id && (v.color_id == selectedColor.value.id || 
                  String(v.color_id) === String(selectedColor.value.id)));
        });
        if (variant) {
          // Don't set price when only color is selected - wait for storage
          selectedVariantPrice.value = null;
          // Only update main image if user hasn't manually selected an image
          if (!userSelectedImage.value) {
            if (variant.variant_images && variant.variant_images.length > 0) {
              active_img.value = variant.variant_images[0];
            } else if (variant.image_url) {
              active_img.value = variant.image_url;
            } else if (variant.image_path) {
              const baseUrl = window.location.origin;
              active_img.value = `${baseUrl}/storage/${variant.image_path}`;
            }
          }
          return variant;
        }
      }
      
      // If only storage is selected, don't set price - wait for color selection
      if (!selectedColor.value && selectedStorage.value) {
        // Clear selected variant since we need both color and storage
        selectedVariant.value = null;
        const variant = variants.find(v => {
          return v.storage_name === selectedStorage.value.name || 
                 (v.storage_id && (v.storage_id == selectedStorage.value.id || 
                  String(v.storage_id) === String(selectedStorage.value.id)));
        });
        if (variant) {
          // Don't set price when only storage is selected - wait for color
          selectedVariantPrice.value = null;
          return variant;
        }
      }

      // If nothing is selected, use base product price
      selectedVariant.value = null;
      selectedVariantPrice.value = null;
      return null;
    };

    // Get available storages for the selected color
    const getAvailableStoragesForColor = () => {
      if (!props.product?.variants || !Array.isArray(props.product.variants)) {
        return props.product?.available_storages || [];
      }

      // If no color is selected, return all storages
      if (!selectedColorId.value) {
        return props.product?.available_storages || [];
      }

      // Filter variants by selected color
      const colorVariants = props.product.variants.filter(v => {
        const colorMatch = v.color_id == selectedColorId.value || 
                          String(v.color_id) === String(selectedColorId.value) ||
                          parseInt(v.color_id) === parseInt(selectedColorId.value);
        const statusMatch = v.status === 'Active' || !v.status;
        return colorMatch && statusMatch;
      });

      // ✅ FIX: Extract unique storages from color variants
      // Use storage_name as key since storage_id can be null
      const storageMap = new Map();

      colorVariants.forEach(variant => {
        if (variant.storage_name) {
          // Use storage_name as key since storage_id might be null
          const key = variant.storage_name;
          if (!storageMap.has(key)) {
            storageMap.set(key, {
              id: variant.storage_id || variant.storage_name, // Use name as id if storage_id is null
              name: variant.storage_name
            });
          }
        }
      });

      const storagesForColor = Array.from(storageMap.values());

      // If this color has no specific storages (or data mismatch), fall back to all storages
      if (storagesForColor.length === 0) {
        return props.product?.available_storages || [];
      }

      return storagesForColor;
    };

    const onColorChange = () => {
      // Reset storage selection if current storage is not available for new color
      if (selectedStorageId.value) {
        const availableStorages = getAvailableStoragesForColor();
        const isStorageAvailable = availableStorages.some(s => {
          return s.id == selectedStorageId.value || 
                 String(s.id) === String(selectedStorageId.value) ||
                 parseInt(s.id) === parseInt(selectedStorageId.value);
        });
        if (!isStorageAvailable) {
          selectedStorageId.value = null;
          selectedStorage.value = null;
        }
      }
      
      // Always update price when color changes (price should update regardless of image selection)
      findMatchingVariant();
    };

    const onStorageChange = () => {
      findMatchingVariant();
    };

    const selectColor = (color) => {
      selectedColorId.value = color.id;
      selectedColor.value = color;
      onColorChange();
    };

    // Use formatPrice from currency composable
    const formatPrice = formatPriceCurrency;

    const isInStock = () => {
      // PRIORITY 1: If variant is selected, check variant quantity (from migrate old product)
      if (selectedVariant.value && selectedVariant.value.quantity !== null && selectedVariant.value.quantity !== undefined) {
        const variantQty = parseInt(selectedVariant.value.quantity);
        if (!isNaN(variantQty) && variantQty > 0) {
          console.log('✅ Variant quantity > 0:', variantQty, '(from migrated product)');
          return true;
        } else if (variantQty === 0) {
          console.log('⚠️ Variant quantity is 0');
          return false;
        }
      }
      
      // PRIORITY 2: Check maximum_quantity (this is the primary stock measure for product)
      const maxQty = props.product?.maximum_quantity;
      if (maxQty !== undefined && maxQty !== null && maxQty !== '') {
        // Convert to number, handling string numbers
        const maxQuantity = parseInt(maxQty);
        if (!isNaN(maxQuantity) && maxQuantity > 0) {
          console.log('✅ Maximum quantity > 0:', maxQuantity, '(primary stock measure)');
          return true;
        } else if (maxQuantity === 0) {
          console.log('⚠️ Maximum quantity is 0');
        }
      }
      
      // PRIORITY 2: Check stock_status from backend (if maximum_quantity not available)
      if (props.product?.stock_status === 'in_stock') {
        console.log('✅ Stock status: in_stock (from backend)');
        return true;
      }
      
      // If stock_status is explicitly 'out_of_stock', return false
      if (props.product?.stock_status === 'out_of_stock') {
        console.log('❌ Stock status: out_of_stock (from backend)');
        return false;
      }
      // PRIORITY 3: Check product-level quantity (from backend calculation)
      const qty = props.product?.quantity;
      if (qty !== undefined && qty !== null && qty !== '') {
        const quantity = parseInt(qty);
        if (!isNaN(quantity) && quantity > 0) {
          console.log('✅ Product quantity > 0:', quantity);
          return true;
        }
      }
      
      // PRIORITY 4: Check variants as fallback
      if (props.product?.variants && props.product.variants.length > 0) {
        // Sum all variant quantities
        let totalVariantQty = 0;
        props.product.variants.forEach(v => {
          const vQty = parseInt(v.quantity || 0);
          if (!isNaN(vQty) && vQty > 0) {
            totalVariantQty += vQty;
          }
        });
        
        if (totalVariantQty > 0) {
          console.log('✅ Total variant quantity > 0:', totalVariantQty);
          return true;
        }
      }
      
      // If we reach here, product is out of stock
      console.log('❌ Product is out of stock - no quantity found');
      return false;
    };

    const getStockStatusClass = () => {
      return isInStock() ? 'in-stock' : 'out-of-stock';
    };

    const getStockStatusText = () => {
      return isInStock() ? 'In Stock' : 'Out of Stock';
    };

    const parseSpecifications = (specString) => {
      if (!specString) return [];
      // Split by newlines or common separators
      return specString.split(/\n|,|;/).filter(s => s.trim().length > 0).slice(0, 6);
    };

    const getMaxQuantity = () => {
      // Priority 1: If variant is selected, use variant quantity (from migrate old product)
      if (selectedVariant.value && selectedVariant.value.quantity !== null && selectedVariant.value.quantity !== undefined) {
        const variantQty = parseInt(selectedVariant.value.quantity);
        if (!isNaN(variantQty) && variantQty >= 0) {
          console.log('📦 Using variant quantity from migrated product:', {
            variant_id: selectedVariant.value.variant_id || selectedVariant.value.id,
            color: selectedVariant.value.color_name,
            storage: selectedVariant.value.storage_name,
            variant_quantity: variantQty
          });
          return variantQty;
        }
      }
      
      // Priority 2: Get maximum available quantity from product
      const maxQty = props.product?.maximum_quantity;
      if (maxQty !== undefined && maxQty !== null && maxQty !== '') {
        const max = parseInt(maxQty);
        if (!isNaN(max) && max > 0) {
          return max;
        }
      }
      
      // Priority 3: Fallback to product quantity
      const qty = props.product?.quantity;
      if (qty !== undefined && qty !== null && qty !== '') {
        const q = parseInt(qty);
        if (!isNaN(q) && q > 0) {
          return q;
        }
      }
      
      // If no stock available, return 0
      return 0;
    };

    const validateQuantity = () => {
      // Ensure quantity is at least 1
      if (quantity.value < 1) {
        quantity.value = 1;
      }
      
      // Ensure quantity doesn't exceed available stock
      const maxQty = getMaxQuantity();
      if (maxQty > 0 && quantity.value > maxQty) {
        quantity.value = maxQty;
        alert(`Maximum available quantity is ${maxQty}`);
      }
    };

    const incrementQuantity = () => {
      const maxQty = getMaxQuantity();
      if (maxQty > 0 && quantity.value < maxQty) {
        quantity.value++;
      } else if (maxQty > 0) {
        alert(`Maximum available quantity is ${maxQty}`);
      }
    };

    const decrementQuantity = () => {
      if (quantity.value > 1) {
        quantity.value--;
      }
    };

    const addToCart = () => {
      // Check if product is in stock
      if (!isInStock()) {
        alert('This product is currently out of stock. Please try again later.');
        return;
      }
      
      // Validate color selection only (storage is optional)
      if (props.product.available_colors && props.product.available_colors.length > 0 && !selectedColorId.value) {
        alert('Please select a color');
        return;
      }
      
      // Storage validation removed - storage is now optional
      // Products can be added to cart without storage selection
      
      const variant = findMatchingVariant();
      const productToAdd = {
        ...props.product,
        price: selectedVariantPrice.value || props.product.price,
        selectedColor: selectedColor.value,
        selectedColorId: selectedColorId.value,
        selectedStorage: selectedStorage.value,
        selectedStorageId: selectedStorageId.value,
        selectedWarranty: selectedWarranty.value,
        quantity: quantity.value,
        variant_id: variant?.variant_id || null,
        variant_code: variant?.variant_code || null
      };
      cartStore.addCartProduct(productToAdd);
    };

    const buyNow = () => {
      addToCart();
      // Navigate to checkout
      window.location.href = '/#/checkout';
    };

    const addToWishlist = () => {
      const p = props.product || {};
      if (!p.id) return;
      const img =
        active_img.value ||
        p.image ||
        (Array.isArray(p.images) && p.images.length ? p.images[0] : '') ||
        '';
      const price = Number(
        selectedVariantPrice.value != null ? selectedVariantPrice.value : p.price ?? p.web_price ?? 0
      );
      const qty = getMaxQuantity();
      wishlistStore.add_wishlist_product({
        id: p.id,
        title: p.product_name || p.title || 'Product',
        image: img,
        price,
        discount_percentage: Number(p.discount_percentage ?? 0),
        quantity: qty > 0 ? qty : Number(p.maximum_quantity ?? p.quantity ?? 0),
      });
    };

    const showFullScreenModal = ref(false);
    const fullScreenImage = ref('');
    
    // Image gallery state
    const imageLoading = ref(false);
    const showZoom = ref(false);
    const thumbnailScrollPosition = ref(0);
    const thumbnailListRef = ref(null);
    const mainImageRef = ref(null);
    const zoomOverlayRef = ref(null);
    const zoomEnabled = ref(false);
    const zoomLevel = ref(1); // 1 = normal, 2 = 2x, 3 = 3x
    
    // Computed property for display price (ensures reactivity)
    const currentDisplayPrice = computed(() => {
      // Access selectedVariant to ensure Vue tracks it as a dependency
      // (Accessing it ensures reactivity even if we don't use it directly)
      void selectedVariant.value; // Track dependency for reactivity
      const variantPrice = selectedVariantPrice.value;
      const productPrice = props.product?.price || props.product?.web_price || 0;
      
      // If variant price is set, use it
      if (variantPrice !== null && variantPrice !== undefined) {
        return variantPrice;
      }
      
      // Otherwise use product price
      return productPrice;
    });
    
    // Check if we have a valid image (not default/placeholder)
    const hasValidImage = computed(() => {
      const img = active_img.value || props.product?.image || '';
      return img && 
             !img.includes('default-product') && 
             !img.includes('placeholder') &&
             img.trim() !== '';
    });
    
    // Get valid image source (filter out default/placeholder images)
    const validImageSrc = computed(() => {
      const img = active_img.value || props.product?.image || '';
      if (img.includes('default-product') || img.includes('placeholder')) {
        return '';
      }
      return img;
    });
    
    // Get current image index
    const currentImageIndex = computed(() => {
      const images = getDisplayImages();
      return images.findIndex(img => img === active_img.value);
    });
    
    // Select image with smooth transition - works for both color-specific and normal images
    const selectImage = (img) => {
      if (!img) return;
      if (img === active_img.value) return;
      
      // IMPORTANT: Set flag FIRST before any other operations
      // This prevents watch and findMatchingVariant from interfering
      userSelectedImage.value = true;
      
      // Set loading state
      imageLoading.value = true;
      
      // Update active image IMMEDIATELY - this will display in main area (medium size)
      // This works for both normal images and color-specific images
      active_img.value = img;
      
      // Update zoom overlay with new image if zoom is active
      if (zoomLevel.value > 1 && zoomOverlayRef.value) {
        zoomOverlayRef.value.style.setProperty('--zoom-image', `url(${img})`);
      }
      
      // Use nextTick to ensure DOM updates after reactive change
      nextTick(() => {
        // Update zoom overlay again after DOM update
        if (zoomLevel.value > 1 && zoomOverlayRef.value) {
          zoomOverlayRef.value.style.setProperty('--zoom-image', `url(${active_img.value})`);
        }
        // Force another update to ensure it sticks (in case watch tried to override)
        if (active_img.value !== img) {
          active_img.value = img;
          userSelectedImage.value = true; // Re-set flag if it was reset
        }
        
        // Scroll thumbnail into view if needed (only for thumbnail navigation)
        if (thumbnailListRef.value) {
          const thumbnails = thumbnailListRef.value.querySelectorAll('.thumbnail-item');
          const thumbnailImages = getThumbnailImages();
          const thumbnailIndex = thumbnailImages.indexOf(img);
          
          // If image is in thumbnail list, scroll it into view
          if (thumbnailIndex !== -1 && thumbnails[thumbnailIndex]) {
            thumbnails[thumbnailIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
        
        // Reset loading state after image loads
        setTimeout(() => {
          imageLoading.value = false;
        }, 300);
        
        // Keep the flag set for longer to prevent any interference from watch or findMatchingVariant
        // Reset after 1 second to allow normal color selection behavior later
        setTimeout(() => {
          userSelectedImage.value = false;
        }, 1000);
      });
    };
    
    // Navigate to previous/next image
    const navigateImage = (direction) => {
      const images = getDisplayImages();
      if (images.length <= 1) return;
      
      const currentIndex = currentImageIndex.value;
      let newIndex;
      
      if (direction === 'prev') {
        newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
      } else {
        newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
      }
      
      selectImage(images[newIndex]);
    };
    
    // Scroll thumbnails
    const scrollThumbnails = (direction) => {
      if (!thumbnailListRef.value) return;
      
      const scrollAmount = 100;
      if (direction === 'up') {
        thumbnailListRef.value.scrollTop -= scrollAmount;
        thumbnailScrollPosition.value = Math.max(0, thumbnailScrollPosition.value - 1);
      } else {
        thumbnailListRef.value.scrollTop += scrollAmount;
        thumbnailScrollPosition.value = Math.min(
          getThumbnailImages().length - 4,
          thumbnailScrollPosition.value + 1
        );
      }
    };
    
    // Handle zoom on mouse move
    const handleZoom = (event) => {
      if (!zoomEnabled.value || !zoomOverlayRef.value) return;
      
      const wrapper = event.currentTarget?.parentElement || event.target?.closest('.main-image-wrapper');
      if (!wrapper) return;
      
      const rect = wrapper.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      
      // Clamp values between 0 and 100
      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));
      
      if (zoomOverlayRef.value) {
        zoomOverlayRef.value.style.setProperty('--zoom-image', `url(${active_img.value})`);
        zoomOverlayRef.value.style.backgroundPosition = `${clampedX}% ${clampedY}%`;
      }
    };
    
    // Zoom in
    const zoomIn = () => {
      if (zoomLevel.value < 3) {
        zoomLevel.value++;
        updateZoom();
      }
    };
    
    // Zoom out
    const zoomOut = () => {
      if (zoomLevel.value > 1) {
        zoomLevel.value--;
        updateZoom();
      }
    };
    
    // Reset zoom
    const resetZoom = () => {
      zoomLevel.value = 1;
      updateZoom();
    };
    
    // Update zoom based on zoom level
    const updateZoom = () => {
      if (zoomLevel.value > 1) {
        zoomEnabled.value = true;
        showZoom.value = true;
        if (zoomOverlayRef.value) {
          const zoomSize = zoomLevel.value * 100; // 200%, 300%
          zoomOverlayRef.value.style.setProperty('--zoom-image', `url(${active_img.value})`);
          zoomOverlayRef.value.style.backgroundSize = `${zoomSize}%`;
          zoomOverlayRef.value.style.opacity = '1';
        }
      } else {
        zoomEnabled.value = false;
        showZoom.value = false;
        if (zoomOverlayRef.value) {
          zoomOverlayRef.value.style.opacity = '0';
        }
      }
    };
    
    // Handle image errors
    const handleImageError = (event) => {
      const imgSrc = event.target?.src || active_img.value || '';
      console.error('Main image failed to load:', imgSrc);
      imageLoading.value = false;
      
      // If it's a default/placeholder image, try to find an alternative
      if (imgSrc.includes('default-product') || imgSrc.includes('placeholder')) {
        const thumbnailImages = getThumbnailImages();
        if (thumbnailImages.length > 0) {
          // Try to use first thumbnail image
          const firstThumb = thumbnailImages.find(img => 
            img && !img.includes('default-product') && !img.includes('placeholder')
          );
          if (firstThumb) {
            active_img.value = firstThumb;
            return;
          }
        }
        // If no alternative found, set to empty to show placeholder
        active_img.value = '';
        return;
      }
      
      // For other errors, try to find an alternative image
      const thumbnailImages = getThumbnailImages();
      if (thumbnailImages.length > 0) {
        const alternative = thumbnailImages.find(img => 
          img && img !== imgSrc && !img.includes('default-product') && !img.includes('placeholder')
        );
        if (alternative) {
          active_img.value = alternative;
        } else {
          active_img.value = '';
        }
      } else {
        active_img.value = '';
      }
    };
    
    // Handle successful image load
    const handleImageLoad = () => {
      imageLoading.value = false;
    };
    
    const handleThumbnailError = (event) => {
      if (event.target) {
        event.target.src = '/images/placeholder-thumb.png';
      }
    };
    
    const openImageModal = () => {
      if (active_img.value) {
        fullScreenImage.value = active_img.value;
        showFullScreenModal.value = true;
      }
    };
    
    const closeImageModal = () => {
      showFullScreenModal.value = false;
      fullScreenImage.value = '';
    };

    const shareOnFacebook = () => {
      const url = encodeURIComponent(window.location.href);
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
    };

    const shareOnTwitter = () => {
      const url = encodeURIComponent(window.location.href);
      const text = encodeURIComponent(props.product.product_name);
      window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank');
    };

    const shareOnWhatsApp = () => {
      const url = encodeURIComponent(window.location.href);
      const text = encodeURIComponent(`Check out ${props.product.product_name}`);
      window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
    };
  
      return {
        active_img,
        cartStore,
      selectedColor,
      selectedColorId,
      selectedStorage,
      selectedStorageId,
      selectedWarranty,
      quantity,
      selectedVariantPrice,
      currentDisplayPrice,
      selectedVariant,
      currencySymbol,
      onColorChange,
      onStorageChange,
      selectColor,
      formatPrice,
      parseSpecifications,
      incrementQuantity,
      decrementQuantity,
      validateQuantity,
      getMaxQuantity,
      addToCart,
      buyNow,
      addToWishlist,
      openImageModal,
      showFullScreenModal,
      fullScreenImage,
      closeImageModal,
      imageLoading,
      showZoom,
      thumbnailScrollPosition,
      thumbnailListRef,
      mainImageRef,
      zoomOverlayRef,
      zoomEnabled,
      hasValidImage,
      validImageSrc,
      currentImageIndex,
      selectImage,
      navigateImage,
      scrollThumbnails,
      handleZoom,
      zoomIn,
      zoomOut,
      resetZoom,
      zoomLevel,
      handleImageError,
      handleImageLoad,
      handleThumbnailError,
      findMatchingVariant,
      getColorImage,
      getVariantImages,
      getDisplayImages,
      getThumbnailImages,
      getAvailableStoragesForColor,
      shareOnFacebook,
      shareOnTwitter,
      shareOnWhatsApp,
      isInStock,
      getStockStatusClass,
      getStockStatusText
    };
    }
  };
  </script>
  <style scoped>
.product-details {
  padding: 40px 0;
  background: #fff;
}

.breadcrumb-nav {
  font-size: 14px;
  color: #666;
  padding: 15px 0;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 30px;
}

.breadcrumb-nav span {
  color: #999;
}

/* Product Image Gallery - Industry Level */
.product-image-gallery {
  display: flex;
  gap: 20px;
  position: relative;
}

/* Thumbnail Navigation */
.thumbnail-navigation {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 90px;
}

.nav-arrow {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #666;
  margin-bottom: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.nav-arrow:hover:not(:disabled) {
  background: var(--fe-secondary, #263C97);
  color: #fff;
  border-color: var(--fe-secondary, #263C97);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px color-mix(in srgb, var(--fe-secondary, #263C97) 40%, transparent);
}

.nav-arrow:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.thumbnail-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px;
  scrollbar-width: thin;
  scrollbar-color: #ddd transparent;
}

.thumbnail-list::-webkit-scrollbar {
  width: 4px;
}

.thumbnail-list::-webkit-scrollbar-track {
  background: transparent;
}

.thumbnail-list::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 2px;
}

.thumbnail-list::-webkit-scrollbar-thumb:hover {
  background: #bbb;
}

.thumbnail-item {
  width: 80px;
  height: 80px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 4px;
  background: #fff;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumbnail-item:hover {
  border-color: var(--fe-secondary, #263C97);
  transform: scale(1.05);
  box-shadow: 0 4px 12px color-mix(in srgb, var(--fe-secondary, #263C97) 32%, transparent);
}

.thumbnail-item.active {
  border-color: var(--fe-secondary, #263C97);
  border-width: 3px;
  box-shadow: 0 4px 16px color-mix(in srgb, var(--fe-secondary, #263C97) 50%, transparent);
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 8%, #ffffff);
}

.thumbnail-image-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
}

.thumbnail-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.thumbnail-item:hover .thumbnail-image-wrapper img {
  transform: scale(1.1);
}

.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  transition: background 0.3s ease;
  pointer-events: none;
}

.thumbnail-item.active .thumbnail-overlay {
  background: color-mix(in srgb, var(--fe-secondary, #263C97) 14%, transparent);
}

/* Main Image Container */
.main-image-container {
  flex: 1;
  position: relative;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  /* Ensure consistent container size for all images */
  min-width: 0;
  min-height: 0;
}

.main-image-wrapper {
  position: relative;
  width: 100%;
  padding: 30px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 560px;
  height: 560px;
  cursor: zoom-in;
  /* Ensure consistent container size for all images */
  box-sizing: border-box;
}

.image-loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--fe-secondary, #263C97);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.main-product-image {
  width: 500px;
  height: 500px;
  object-fit: contain;
  object-position: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 8px;
  display: block;
  margin: 0 auto;
  /* Fixed size for all images - normal and color-specific */
  flex-shrink: 0;
}

.main-product-image.zoom-enabled {
  cursor: zoom-in;
}

/* No Image Placeholder */
.no-image-placeholder {
  width: 100%;
  height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  background: #f8f9fa;
  border-radius: 8px;
}

.no-image-placeholder i {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.no-image-placeholder p {
  font-size: 16px;
  color: #666;
  margin: 0;
}

.image-zoom-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: var(--zoom-image);
  background-size: 200%;
  background-repeat: no-repeat;
  opacity: 0;
  transition: opacity 0.3s ease, background-size 0.3s ease;
  pointer-events: none;
  border-radius: 8px;
  z-index: 10;
}

.image-zoom-overlay.zoom-active {
  pointer-events: auto;
  cursor: zoom-out;
}

.image-zoom-overlay.zoom-active {
  opacity: 1;
}

.main-image-wrapper:hover .image-zoom-overlay.zoom-active {
  opacity: 1;
}
/* Image Navigation Arrows */
.image-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255,255,255,0.95);
  border: 1px solid #e0e0e0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #333;
  z-index: 5;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.image-nav-btn:hover {
  background: var(--fe-secondary, #263C97);
  color: #fff;
  border-color: var(--fe-secondary, #263C97);
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 4px 12px color-mix(in srgb, var(--fe-secondary, #263C97) 50%, transparent);
}

.image-nav-prev {
  left: 15px;
}

.image-nav-next {
  right: 15px;
}

/* Image Actions */
.image-actions {
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
  gap: 10px;
  z-index: 20;
  pointer-events: auto;
}

.action-btn {
  background: rgba(255,255,255,0.95);
  border: 1px solid #e0e0e0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #333;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.action-btn:hover {
  background: var(--fe-secondary, #263C97);
  color: #fff;
  border-color: var(--fe-secondary, #263C97);
  transform: scale(1.1);
  box-shadow: 0 4px 12px color-mix(in srgb, var(--fe-secondary, #263C97) 50%, transparent);
}

.action-btn.active {
  background: var(--fe-secondary, #263C97);
  color: #fff;
  border-color: var(--fe-secondary, #263C97);
  box-shadow: 0 4px 12px color-mix(in srgb, var(--fe-secondary, #263C97) 50%, transparent);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* Image Counter */
.image-counter {
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(0,0,0,0.7);
  color: #fff;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  z-index: 5;
  backdrop-filter: blur(10px);
}

/* Color Thumbnails */
.color-thumbnails {
  margin-top: 20px;
}

.color-thumb {
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  padding: 8px;
  border-radius: 8px;
  border: 2px solid #ddd;
  background: #fff;
}

.color-thumb:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.color-thumb.active {
  border-color: var(--fe-secondary, #263C97);
  border-width: 3px;
  box-shadow: 0 4px 12px color-mix(in srgb, var(--fe-secondary, #263C97) 50%, transparent);
}

.color-thumb-image-wrapper {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
  justify-content: center;
}

.color-thumb-image {
  width: 60px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f5f5;
  position: relative;
}

.color-thumb-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.color-thumb-image-duplicate {
  transform: rotate(-15deg);
  opacity: 0.9;
}

.color-thumb-label {
  font-size: 12px;
  font-weight: 500;
  color: #333;
}

/* Product Title */
.product-title-section {
  margin-bottom: 20px;
}

.discount-badge {
  background: var(--fe-secondary, #263C97);
  color: white;
  font-size: 13px;
  padding: 6px 14px;
  border-radius: 50px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 55px;
  margin-right: 8px;
}

.new-badge {
  background: #5aab19;
  color: white;
  font-size: 12px;
  padding: 6px 14px;
  border-radius: 50px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.product-name {
  font-size: 22px;
  font-weight: 700;
  color: #333;
  line-height: 1.3;
  margin-top: 10px;
}

.product-brand-logo {
  font-size: 24px;
  color: #666;
  opacity: 0.7;
}

/* Price Section */
.price-section {
  margin-bottom: 20px;
}

.price-section__label {
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #64748b;
}

.current-price {
  font-size: clamp(1.5rem, 3.5vw, 2rem);
  font-weight: 800;
  color: var(--fe-secondary, #263C97);
  line-height: 1.15;
  letter-spacing: -0.03em;
}

.original-price {
  font-size: clamp(0.9rem, 2vw, 1.15rem);
  font-weight: 600;
  color: #64748b;
  margin-left: 12px;
}

/* Stock Status */
.stock-badge {
  font-size: 14px;
  padding: 12px 24px;
  font-weight: 600;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
}

.stock-badge.in-stock {
  background: #5aab19;
  color: white;
  font-size: 14px;
  padding: 10px 20px;
  border-radius: 6px;
}

.stock-badge.out-of-stock {
  background: #CC1414;
  color: white;
  font-size: 14px;
  padding: 10px 20px;
  border-radius: 6px;
}

.stock-badge i {
  font-size: 16px;
}

/* Specifications */
.specs-title {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
}

.specification-list {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  padding: 20px;
  border-radius: 10px;
  border-left: 4px solid var(--fe-secondary, #263C97);
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  list-style: none;
  margin: 0;
}

.specification-list li {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
  font-size: 14px;
}

.specification-list li:last-child {
  border-bottom: none;
}

.specification-list li i {
  color: var(--fe-secondary, #263C97);
  font-size: 12px;
}

/* Selection Groups */
.selection-group {
  margin-bottom: 20px;
}

.form-label {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  display: block;
}

.selection-input {
  border-radius: 6px;
  border: 1px solid #ddd;
  padding: 12px 15px;
  font-size: 15px;
  transition: all 0.3s;
  max-width: 400px;
  width: 100%;
}

.selection-input:focus {
  border-color: var(--fe-secondary, #263C97);
  box-shadow: 0 0 0 0.2rem color-mix(in srgb, var(--fe-secondary, #263C97) 14%, transparent);
  outline: none;
}

/* Quantity Selector */
.quantity-selector {
  display: flex;
  align-items: center;
  max-width: 180px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}

.qty-btn {
  width: 50px;
  height: 50px;
  border: none;
  background: #f8f9fa;
  font-size: 22px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #ddd;
}

.qty-btn:last-of-type {
  border-right: none;
  border-left: 1px solid #ddd;
}

.qty-btn:hover:not(:disabled) {
  background: #e9ecef;
}

.qty-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.qty-input {
  flex: 1;
  height: 50px;
  border: none;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  background: #fff;
  padding: 0;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.action-btn {
  font-weight: 700;
  padding: 16px 32px;
  border-radius: 6px;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  font-size: 15px;
  min-width: 180px;
  flex: 1;
  border: none;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.action-buttons .btn.btn-primary,
.action-buttons .action-btn {
  color: #ffffff !important;
}

.add-cart-btn {
  background: var(--fe-secondary, #263C97);
  color: #ffffff !important;
}

.add-cart-btn:hover:not(:disabled) {
  background: var(--fe-secondary, #263C97);
  color: #ffffff !important;
  filter: brightness(1.1);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px color-mix(in srgb, var(--fe-secondary, #263C97) 48%, transparent);
}

.action-buttons .buy-now-btn.btn-primary.action-btn,
.action-buttons .buy-now-btn.action-btn {
  background: #ffffff !important;
  color: #141414 !important;
  border: 2px solid var(--fe-accent, #fcbe00) !important;
  box-shadow:
    0 4px 18px rgba(15, 23, 42, 0.12),
    0 0 0 1px rgba(15, 23, 42, 0.06);
}

.buy-now-btn:hover:not(:disabled) {
  background: #f8fafc !important;
  color: #0a0a0a !important;
  transform: translateY(-2px);
  border-color: var(--fe-accent-hover, #e5ac00) !important;
  box-shadow:
    0 8px 24px rgba(15, 23, 42, 0.14),
    0 0 0 1px color-mix(in srgb, var(--fe-accent, #fcbe00) 35%, transparent);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #ccc !important;
  transform: none !important;
  box-shadow: none !important;
}

/* Wishlist */
.wishlist-link {
  margin-bottom: 25px;
}

.wishlist-link-text {
  color: var(--fe-secondary-light, #5a9fd4);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  padding: 8px 0;
  transition: all 0.3s;
  font-size: 15px;
  font-weight: 500;
}

.wishlist-link-text:hover {
  color: var(--fe-accent, #fcbe00);
  text-decoration: underline;
}

.wishlist-link-text i {
  margin-right: 6px;
  font-size: 16px;
}

/* Product Info */
.product-info-section {
  margin-bottom: 25px;
}

.product-info-item {
  font-size: 15px;
  color: #333;
}

.info-label {
  font-weight: 600;
  margin-right: 8px;
}

.info-value {
  color: #666;
}

.info-label {
  color: #333;
  font-weight: 600;
  margin-right: 8px;
}

.info-value {
  color: #666;
}

/* Delivery Information */
.delivery-info-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-top: 20px;
}

.delivery-title {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
}

.delivery-item {
  padding: 8px 0;
  font-size: 14px;
  color: #333;
  display: flex;
  align-items: center;
}

.delivery-item i {
  width: 20px;
  margin-right: 10px;
  text-align: center;
  color: var(--fe-secondary, #263C97);
}

/* Share Section */
.share-section {
  margin-top: 0;
}

.share-title {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
  margin-bottom: 12px;
}

.share-buttons {
  display: flex;
  gap: 10px;
}

.share-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: all 0.3s;
  font-size: 18px;
}

.share-btn.facebook {
  background: #1877f2;
  color: white;
}

.share-btn.twitter {
  background: #1da1f2;
  color: white;
}

.share-btn.whatsapp {
  background: #25d366;
  color: white;
}

.share-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}

/* Variant Images Gallery - Industry Level */
.variant-images-gallery {
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid #e9ecef;
}

.gallery-title {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.gallery-title i {
  color: var(--fe-secondary, #263C97);
}

.variant-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 80px);
  gap: 15px;
  justify-content: start;
}

.variant-image-item {
  position: relative;
  width: 80px;
  height: 80px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #fff;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.variant-image-item:hover {
  border-color: var(--fe-secondary, #263C97);
  transform: scale(1.05);
  box-shadow: 0 4px 12px color-mix(in srgb, var(--fe-secondary, #263C97) 32%, transparent);
}

.variant-image-item.active {
  border-color: var(--fe-secondary, #263C97);
  border-width: 3px;
  box-shadow: 0 8px 24px color-mix(in srgb, var(--fe-secondary, #263C97) 50%, transparent);
}

.variant-image-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 4px;
}

.variant-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.variant-image-item:hover .variant-image-wrapper img {
  transform: scale(1.1);
}

.variant-image-item:hover {
  transform: scale(1.05);
}

.variant-image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  pointer-events: none;
}

.variant-image-item:hover .variant-image-overlay {
  background: color-mix(in srgb, var(--fe-secondary, #263C97) 14%, transparent);
}

.variant-image-overlay i {
  color: #fff;
  font-size: 24px;
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.3s ease;
}

.variant-image-item:hover .variant-image-overlay i {
  opacity: 1;
  transform: scale(1);
}

/* Full Screen Image Modal */
.fullscreen-image-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.fullscreen-image-container {
  position: relative;
  max-width: 90%;
  max-height: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fullscreen-image {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.close-fullscreen-btn {
  position: absolute;
  top: -50px;
  right: 0;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;
  color: #333;
  transition: all 0.3s;
  z-index: 10000;
}

.close-fullscreen-btn:hover {
  background: rgba(255, 255, 255, 1);
  transform: scale(1.1);
}

/* Responsive Styles */
@media (max-width: 768px) {
  .product-image-gallery {
    flex-direction: column;
    gap: 15px;
  }
  
  .thumbnail-navigation {
    flex-direction: row;
    min-width: 100%;
    overflow-x: auto;
    padding-bottom: 10px;
  }
  
  .thumbnail-list {
    flex-direction: row;
    max-height: none;
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    gap: 10px;
  }
  
  .nav-arrow {
    display: none;
  }
  
  .main-image-container {
    width: 100%;
  }
  
  .main-image-wrapper {
    min-height: 360px;
    height: 360px;
    padding: 15px;
  }
  
  .main-product-image {
    width: 330px;
    height: 330px;
    object-fit: contain;
    object-position: center;
    display: block;
    margin: 0 auto;
    /* Fixed size for all images - normal and color-specific */
    flex-shrink: 0;
  }
  
  .image-nav-btn {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }
  
  .image-actions {
    bottom: 15px;
    right: 15px;
  }
  
  .action-btn {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }
  
  .variant-images-grid {
    grid-template-columns: repeat(auto-fill, 80px);
    gap: 10px;
    justify-content: start;
  }
  
  .variant-image-item {
    width: 80px;
    height: 80px;
  }
  
  .product-name {
    font-size: 18px;
  }
  
  .current-price {
    font-size: clamp(1.3rem, 4vw, 1.6rem);
  }

  .original-price {
    font-size: clamp(1rem, 3.8vw, 1.25rem);
    margin-left: 0;
    margin-top: 0.25rem;
    display: inline-block;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .action-btn {
    width: 100%;
  }
}
</style>